import { useState, useMemo } from "react";
import { useNavigate } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  type SortingState,
  type ColumnDef,
  flexRender,
} from "@tanstack/react-table";
import {
  Plus,
  Search,
  Building2,
  Pencil,
  Table2,
  Columns3,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useTranslation } from "@/i18n/use-translation";
import type { TFunction } from "@/i18n/catalog";
import { formatDate } from "@/lib/format";
import { ConfigOverrides } from "@/components/config-overrides";
import { ExportButton } from "@/components/export-button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { KanbanBoard } from "@/components/kanban-board";

const PAGE_SIZE = 10;

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

interface Account {
  id: string;
  org_id: string;
  name: string;
  slug: string;
  contact_name: string | null;
  contact_email: string | null;
  status: string;
  currency: string | null;
  timezone: string | null;
  default_weight_unit: string | null;
  waste_pct_target: number | null;
  waste_per_cover_target_g: number | null;
  contract_start: string | null;
  contract_end: string | null;
  created_at: string;
}

/** Built inside the component and memoised on `t`, so the messages are the reader's — a
 * module-level schema would freeze whichever locale happened to be active at import
 * (POS2-1133 pinned decision, the `makeSchema(t)` factory CP3a landed). */
function makeCreateAccountSchema(t: TFunction) {
  return z.object({
    name: z.string().min(1, t("validation.nameRequired")),
    contact_name: z.string().optional(),
    contact_email: z.string().email(t("validation.invalidEmail")).or(z.literal("")).optional(),
    currency: z.string().optional(),
    timezone: z.string().optional(),
    default_weight_unit: z.string().optional(),
    waste_pct_target: z
      .string()
      .optional()
      .refine((v) => !v || (Number(v) >= 0 && Number(v) <= 100), "Must be between 0 and 100"),
    waste_per_cover_target_g: z
      .string()
      .optional()
      .refine((v) => !v || (Number.isInteger(Number(v)) && Number(v) > 0), "Whole grams above 0"),
  });
}

type CreateAccountForm = z.infer<ReturnType<typeof makeCreateAccountSchema>>;

const STATUS_STYLES: Record<string, string> = {
  active: "border-transparent bg-emerald-50 text-emerald-700",
  inactive: "border-transparent bg-gray-50 text-gray-400",
  suspended: "border-transparent bg-orange-50 text-orange-700",
  ended: "border-transparent bg-red-50 text-red-700",
};

const STATUSES = ["active", "inactive", "suspended", "ended"] as const;

export default function SettingsAccountsPage() {
  const { locale, t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Account | null>(null);
  const [view, setView] = useState<"table" | "kanban">("table");
  const [sorting, setSorting] = useState<SortingState>([]);

  const accountsQuery = useQuery<Account[]>({
    queryKey: ["accounts"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/accounts`, { headers });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status}: ${text || res.statusText}`);
      }
      return (await res.json()).data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });

  const filtered = useMemo(() => {
    if (!accountsQuery.data) return [];
    if (!search.trim()) return accountsQuery.data;
    const q = search.toLowerCase();
    return accountsQuery.data.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        a.contact_name?.toLowerCase().includes(q) ||
        a.contact_email?.toLowerCase().includes(q),
    );
  }, [accountsQuery.data, search]);

  const createMutation = useMutation({
    mutationFn: async (data: CreateAccountForm) => {
      const res = await fetch(`${API_URL}/api/v1/accounts`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          ...data,
          contact_name: data.contact_name || null,
          contact_email: data.contact_email || null,
          currency: data.currency || null,
          timezone: data.timezone || null,
          default_weight_unit: data.default_weight_unit || null,
          waste_pct_target: data.waste_pct_target ? Number(data.waste_pct_target) : null,
          waste_per_cover_target_g: data.waste_per_cover_target_g
            ? Number(data.waste_per_cover_target_g)
            : null,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message ?? "Failed to create account");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      toast.success(t("settings.accountCreated"));
      setOpen(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const editMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CreateAccountForm }) => {
      const res = await fetch(`${API_URL}/api/v1/accounts/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          name: data.name,
          contact_name: data.contact_name || null,
          contact_email: data.contact_email || null,
          currency: data.currency || null,
          timezone: data.timezone || null,
          default_weight_unit: data.default_weight_unit || null,
          waste_pct_target: data.waste_pct_target ? Number(data.waste_pct_target) : null,
          waste_per_cover_target_g: data.waste_per_cover_target_g
            ? Number(data.waste_per_cover_target_g)
            : null,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message ?? "Failed to update account");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      toast.success(t("settings.accountUpdated"));
      setEditing(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const res = await fetch(`${API_URL}/api/v1/accounts/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ status }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message ?? "Failed to update status");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const form = useForm<CreateAccountForm>({
    resolver: zodResolver(useMemo(() => makeCreateAccountSchema(t), [t])),
  });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = form;

  const navigate = useNavigate();

  const columns = useMemo<ColumnDef<Account>[]>(
    () => [
      {
        accessorKey: "name",
        header: ({ column }) => (
          <span
            role="button"
            tabIndex={0}
            className="inline-flex cursor-pointer select-none items-center gap-1"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ")
                column.toggleSorting(column.getIsSorted() === "asc");
            }}
          >
            {t("settings.name")}
            <ArrowUpDown className="size-3 text-muted-foreground/50" />
          </span>
        ),
        cell: ({ row }) => (
          <button
            className="text-sm font-medium hover:underline"
            onClick={() => navigate(`/settings/sites?account=${row.original.id}`)}
          >
            {row.original.name}
          </button>
        ),
      },
      {
        accessorKey: "contact_name",
        header: "Contact",
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {row.original.contact_name ?? row.original.contact_email ?? "—"}
          </span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <Badge
            className={
              STATUS_STYLES[row.original.status] ?? "border-transparent bg-gray-100 text-gray-600"
            }
          >
            {row.original.status}
          </Badge>
        ),
      },
      {
        accessorKey: "created_at",
        header: ({ column }) => (
          <span
            role="button"
            tabIndex={0}
            className="inline-flex cursor-pointer select-none items-center gap-1"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ")
                column.toggleSorting(column.getIsSorted() === "asc");
            }}
          >
            {t("settings.columnCreated")}
            <ArrowUpDown className="size-3 text-muted-foreground/50" />
          </span>
        ),
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {formatDate(locale, row.original.created_at)}
          </span>
        ),
      },
      {
        id: "actions",
        cell: ({ row }) => (
          <Button
            size="icon"
            variant="ghost"
            className="size-7"
            onClick={() => setEditing(row.original)}
          >
            <Pencil className="size-3.5" />
          </Button>
        ),
      },
    ],
    [navigate, locale, t],
  );

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: PAGE_SIZE } },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-medium">{t("settings.accountsTitle")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("settings.accountsDescription")}</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton entity="accounts" />
          <Dialog
            open={open}
            onOpenChange={(v) => {
              setOpen(v);
              if (!v) reset();
            }}
          >
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="mr-1 size-4" />
                {t("settings.newAccount")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t("settings.createAccount")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit((d) => createMutation.mutate(d))} className="space-y-4">
                <div>
                  <label className="text-sm font-medium" htmlFor="name">
                    {t("settings.accountName")}
                  </label>
                  <Input
                    id="name"
                    placeholder={t("settings.accountNamePlaceholder")}
                    {...register("name")}
                  />
                  {errors.name && (
                    <p className="mt-1 text-xs text-destructive">{errors.name.message}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium" htmlFor="contact_name">
                      {t("settings.contactName")}
                    </label>
                    <Input id="contact_name" {...register("contact_name")} />
                  </div>
                  <div>
                    <label className="text-sm font-medium" htmlFor="contact_email">
                      {t("settings.contactEmail")}
                    </label>
                    <Input id="contact_email" type="email" {...register("contact_email")} />
                    {errors.contact_email && (
                      <p className="mt-1 text-xs text-destructive">
                        {errors.contact_email.message}
                      </p>
                    )}
                  </div>
                </div>
                <ConfigOverrides
                  entityType="account"
                  entityId={null}
                  currentCurrency={form.watch("currency")}
                  currentTimezone={form.watch("timezone")}
                  currentWeightUnit={form.watch("default_weight_unit")}
                  currentWastePctTarget={form.watch("waste_pct_target")}
                  currentWastePerCoverTarget={form.watch("waste_per_cover_target_g")}
                  onCurrencyChange={(v) => form.setValue("currency", v)}
                  onTimezoneChange={(v) => form.setValue("timezone", v)}
                  onWeightUnitChange={(v) => form.setValue("default_weight_unit", v)}
                  onWastePctTargetChange={(v) =>
                    form.setValue("waste_pct_target", v, { shouldValidate: true })
                  }
                  onWastePerCoverTargetChange={(v) =>
                    form.setValue("waste_per_cover_target_g", v, { shouldValidate: true })
                  }
                  wastePctTargetError={errors.waste_pct_target?.message}
                  wastePerCoverTargetError={errors.waste_per_cover_target_g?.message}
                />
                <Button
                  type="submit"
                  className="w-full"
                  disabled={!isValid || createMutation.isPending}
                >
                  {createMutation.isPending ? t("settings.creating") : t("settings.createAccount")}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {accountsQuery.data && accountsQuery.data.length > 0 && (
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t("settings.searchAccounts")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center rounded-md border border-gray-200">
            <Button
              size="sm"
              variant={view === "table" ? "secondary" : "ghost"}
              className="h-8 rounded-r-none px-2.5"
              onClick={() => setView("table")}
            >
              <Table2 className="size-4" />
            </Button>
            <Button
              size="sm"
              variant={view === "kanban" ? "secondary" : "ghost"}
              className="h-8 rounded-l-none px-2.5"
              onClick={() => setView("kanban")}
            >
              <Columns3 className="size-4" />
            </Button>
          </div>
        </div>
      )}

      {accountsQuery.isLoading && (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-10 w-full rounded" />
          ))}
        </div>
      )}

      {accountsQuery.isError && (
        <div className="rounded border border-destructive/50 bg-destructive/10 p-3">
          <p className="text-sm font-medium text-destructive">{t("settings.accountsLoadFailed")}</p>
          <p className="mt-1 font-mono text-xs text-destructive/80">
            {accountsQuery.error instanceof Error
              ? accountsQuery.error.message
              : t("settings.unknownError")}
          </p>
        </div>
      )}

      {accountsQuery.data && accountsQuery.data.length === 0 && (
        <div className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
          <Building2 className="size-10 text-gray-300" />
          <p className="text-sm">{t("settings.noAccounts")}</p>
          <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
            {t("settings.createFirstAccount")}
          </Button>
        </div>
      )}

      {filtered.length > 0 && view === "table" && (
        <>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((hg) => (
                <TableRow key={hg.id}>
                  {hg.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {table.getPageCount() > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-xs text-muted-foreground">
                {t("settings.pageOf", {
                  page: table.getState().pagination.pageIndex + 1,
                  total: table.getPageCount(),
                })}
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="size-8"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  <ChevronLeft className="size-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="size-8"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {filtered.length > 0 && view === "kanban" && (
        <KanbanBoard
          statuses={STATUSES}
          statusStyles={STATUS_STYLES}
          items={filtered}
          getStatus={(a) => a.status}
          getId={(a) => a.id}
          onStatusChange={(id, status) => statusMutation.mutate({ id, status })}
          renderCard={(account) => (
            <>
              <button
                className="text-left text-sm font-medium hover:underline"
                onClick={() => navigate(`/settings/sites?account=${account.id}`)}
              >
                {account.name}
              </button>
              <p className="mt-1 truncate text-xs text-muted-foreground">
                {account.contact_name ?? account.contact_email ?? "No contact"}
              </p>
              <div className="mt-2 flex items-center justify-end">
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-6"
                  onClick={() => setEditing(account)}
                >
                  <Pencil className="size-3" />
                </Button>
              </div>
            </>
          )}
        />
      )}

      <EditAccountDialog
        account={editing}
        onClose={() => setEditing(null)}
        onSave={(data) => editing && editMutation.mutate({ id: editing.id, data })}
        isPending={editMutation.isPending}
      />

      {search && filtered.length === 0 && accountsQuery.data && accountsQuery.data.length > 0 && (
        <div className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
          <p className="text-sm">{t("settings.noResultsFor", { search })}</p>
          <Button size="sm" variant="outline" onClick={() => setSearch("")}>
            {t("settings.clearSearch")}
          </Button>
        </div>
      )}
    </div>
  );
}

function EditAccountDialog({
  account,
  onClose,
  onSave,
  isPending,
}: {
  account: Account | null;
  onClose: () => void;
  onSave: (data: CreateAccountForm) => void;
  isPending: boolean;
}) {
  const { t } = useTranslation();
  const form = useForm<CreateAccountForm>({
    resolver: zodResolver(useMemo(() => makeCreateAccountSchema(t), [t])),
  });

  const [prevId, setPrevId] = useState<string | null>(null);
  if (account && account.id !== prevId) {
    setPrevId(account.id);
    form.reset({
      name: account.name,
      contact_name: account.contact_name ?? "",
      contact_email: account.contact_email ?? "",
      currency: account.currency ?? "",
      timezone: account.timezone ?? "",
      default_weight_unit: account.default_weight_unit ?? "",
      waste_pct_target: account.waste_pct_target != null ? String(account.waste_pct_target) : "",
      waste_per_cover_target_g:
        account.waste_per_cover_target_g != null ? String(account.waste_per_cover_target_g) : "",
    });
  }

  return (
    <Dialog open={!!account} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("settings.editAccount")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSave)} className="space-y-4">
          <div>
            <label className="text-sm font-medium" htmlFor="edit-name">
              {t("settings.accountName")}
            </label>
            <Input id="edit-name" {...form.register("name")} />
            {form.formState.errors.name && (
              <p className="mt-1 text-xs text-destructive">{form.formState.errors.name.message}</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium" htmlFor="edit-contact_name">
                {t("settings.contactName")}
              </label>
              <Input id="edit-contact_name" {...form.register("contact_name")} />
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="edit-contact_email">
                {t("settings.contactEmail")}
              </label>
              <Input id="edit-contact_email" type="email" {...form.register("contact_email")} />
              {form.formState.errors.contact_email && (
                <p className="mt-1 text-xs text-destructive">
                  {form.formState.errors.contact_email.message}
                </p>
              )}
            </div>
          </div>
          <ConfigOverrides
            entityType="account"
            entityId={account?.id ?? null}
            currentCurrency={form.watch("currency")}
            currentTimezone={form.watch("timezone")}
            currentWeightUnit={form.watch("default_weight_unit")}
            currentWastePctTarget={form.watch("waste_pct_target")}
            currentWastePerCoverTarget={form.watch("waste_per_cover_target_g")}
            onCurrencyChange={(v) => form.setValue("currency", v)}
            onTimezoneChange={(v) => form.setValue("timezone", v)}
            onWeightUnitChange={(v) => form.setValue("default_weight_unit", v)}
            onWastePctTargetChange={(v) =>
              form.setValue("waste_pct_target", v, { shouldValidate: true })
            }
            onWastePerCoverTargetChange={(v) =>
              form.setValue("waste_per_cover_target_g", v, { shouldValidate: true })
            }
            wastePctTargetError={form.formState.errors.waste_pct_target?.message}
            wastePerCoverTargetError={form.formState.errors.waste_per_cover_target_g?.message}
          />
          <Button type="submit" className="w-full" disabled={isPending}>
            {isPending ? t("common.saving") : t("settings.saveChangesButton")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
